import { invariantResponse } from '@epic-web/invariant'
import { data, NavLink, Outlet, useLoaderData } from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Badge, type Variant } from '#app/components/ui/badge'
import { Icon } from '#app/components/ui/icon.tsx'
import { Separator } from '#app/components/ui/separator'
import { prisma } from '#app/utils/db.server.ts'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalAdminUser } from '#app/utils/user.ts'

type MeetingState = 'general' | 'executive'

export async function loader() {
	const meetings = await prisma.meeting.findMany({
		select: {
			id: true,
			date: true,
			executive: true,
		},
		orderBy: { date: 'desc' },
	})

	invariantResponse(meetings, 'No meetings found', { status: 404 })

	return data(meetings)
}

export default function MeetingsRoute() {
	const meetings = useLoaderData<typeof loader>()
	const canCreate = useOptionalAdminUser()
	const visibleMeetings = meetings.filter(
		(meeting) => !meeting.executive || canCreate,
	)
	const navLinkDefaultClassName =
		'line-clamp-2 flex flex-row flex-wrap items-center gap-3 rounded-l-full py-2 pl-8 pr-6 text-base lg:text-xl'
	return (
		<main className="container flex min-h-[calc(100vh-11rem)] flex-1 px-0 pb-12 md:px-8">
			<div className="bg-muted grid w-full grid-cols-4 pl-2 md:container md:rounded-3xl md:pr-0">
				<div className="relative col-span-1 min-h-[calc(100vh-11rem)] py-6">
					<div className="flex h-full flex-col">
						<h1 className="text-center text-base font-bold md:text-lg lg:text-left lg:text-2xl">
							Meetings:
						</h1>
						<Separator className="mt-4 mb-2" />
						<ul className="flex-1 overflow-x-hidden overflow-y-auto pb-12">
							{canCreate ? (
								<li className="p-1 pr-0">
									<NavLink
										to="new"
										className={({ isActive }) =>
											cn(navLinkDefaultClassName, isActive && 'bg-accent')
										}
									>
										<Icon name="plus">New Meeting</Icon>
									</NavLink>
								</li>
							) : null}
							{visibleMeetings.map(({ id, date, executive }) => {
								const state = executive ? 'executive' : 'general'
								return (
									<li key={id} className="p-1 pr-0">
										<NavLink
											to={date}
											preventScrollReset
											prefetch="intent"
											className={({ isActive }) =>
												cn(navLinkDefaultClassName, isActive && 'bg-accent')
											}
										>
											<span className="min-w-32">{date}</span>
											<Badge
												className={getClassnamesForState(state as MeetingState)}
												variant={getVariantForState(state as MeetingState)}
											>
												{state}
											</Badge>
										</NavLink>
									</li>
								)
							})}
						</ul>
					</div>
				</div>
				<div className="bg-accent relative col-span-3 min-h-[calc(100vh-11rem)] md:rounded-r-3xl">
					<Outlet />
				</div>
			</div>
		</main>
	)
}

function getVariantForState(state: MeetingState): Variant | undefined {
	const map: Partial<Record<MeetingState, Variant>> = {
		general: 'outline',
		executive: 'destructive',
	}
	return map[state]
}

function getClassnamesForState(state: MeetingState): string | undefined {
	const map: Partial<Record<MeetingState, string>> = {
		general: 'border-1 border-primary text-primary',
		executive: 'border-1 border-destructive',
	}
	return cn('ml-2 capitalize', map[state])
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: () => <p>No Meetings found...</p>,
			}}
		/>
	)
}
